import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('signa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('signa_token');
      localStorage.removeItem('signa_user');
      // Don't redirect if on public pages
      const publicPaths = ['/', '/login', '/register', '/p/', '/shop'];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.some(path => 
        path === currentPath || (path === '/p/' && currentPath.startsWith('/p/')) || (path === '/shop' && currentPath.startsWith('/shop'))
      );
      if (!isPublicPage) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Organizations
export const orgAPI = {
  list: () => api.get('/organizations'),
  get: (orgId) => api.get(`/organizations/${orgId}`),
  create: (data) => api.post('/organizations', data),
  update: (orgId, data) => api.patch(`/organizations/${orgId}`, data),
};

// Users
export const userAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  updateStatus: (userId, status) => api.patch(`/users/${userId}/status?status=${status}`),
};

// Profiles
export const profileAPI = {
  list: () => api.get('/profiles'),
  get: (profileId) => api.get(`/profiles/${profileId}`),
  create: (data) => api.post('/profiles', data),
  update: (profileId, data) => api.patch(`/profiles/${profileId}`, data),
  delete: (profileId) => api.delete(`/profiles/${profileId}`),
  getPublic: (profileId) => api.get(`/p/${profileId}`),
};

// Services
export const serviceAPI = {
  list: (profileId) => api.get(`/profiles/${profileId}/services`),
  create: (profileId, data) => api.post(`/profiles/${profileId}/services`, data),
  delete: (profileId, serviceId) => api.delete(`/profiles/${profileId}/services/${serviceId}`),
};

// Media (File Upload)
export const mediaAPI = {
  get: (profileId) => api.get(`/profiles/${profileId}/media`),
  uploadVideo: (profileId, formData) => api.post(`/profiles/${profileId}/videos/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadPhoto: (profileId, formData) => api.post(`/profiles/${profileId}/photos/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (profileId, mediaId) => api.delete(`/profiles/${profileId}/media/${mediaId}`),
  uploadProfilePhoto: (profileId, formData) => api.post(`/profiles/${profileId}/photo/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProfilePhoto: (profileId) => api.delete(`/profiles/${profileId}/photo`),
  uploadLogo: (profileId, formData) => api.post(`/profiles/${profileId}/logo/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: (profileId) => api.delete(`/profiles/${profileId}/logo`),
};

// Artifacts
export const artifactAPI = {
  list: () => api.get('/artifacts'),
  update: (artifactId, data) => {
    const body = typeof data === 'object' && data !== null ? data : { assigned_profile_id: data || null };
    return api.patch(`/artifacts/${artifactId}`, body);
  },
  bulkAssignCampaign: (artifactIds, campaignName) => api.post('/artifacts/bulk-assign-campaign', { artifact_ids: artifactIds, campaign_name: campaignName }),
  // Admin endpoints
  adminList: () => api.get('/admin/artifacts'),
  adminBulkCreate: (data) => api.post('/admin/artifacts/bulk-create', data),
  adminAssign: (artifactId, userId) => api.patch(`/admin/artifacts/${artifactId}/assign`, { user_id: userId }),
  adminDelete: (artifactId) => api.delete(`/admin/artifacts/${artifactId}`),
};

// Cube Config
export const cubeAPI = {
  get: () => api.get('/cube-config'),
  update: (data) => api.patch('/cube-config', data),
};

// Leads
export const leadAPI = {
  list: () => api.get('/leads'),
  create: (profileId, data) => api.post(`/profiles/${profileId}/leads`, data),
  update: (leadId, data) => api.patch(`/leads/${leadId}`, data),
  export: () => api.get('/leads/export'),
};

// Analytics
export const analyticsAPI = {
  get: () => api.get('/analytics'),
  getProfile: (profileId) => api.get(`/analytics/profile/${profileId}`),
  trackEvent: (data) => api.post('/analytics/event', data),
};

// Billing
export const billingAPI = {
  get: () => api.get('/billing'),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
};

// Workflow
export const workflowAPI = {
  get: (profileId) => api.get(`/profiles/${profileId}/workflow`),
  update: (profileId, data) => api.put(`/profiles/${profileId}/workflow`, data),
  getPublic: (profileId) => api.get(`/profiles/${profileId}/workflow/public`),
};

// Workflow Analytics (public, no auth)
export const workflowAnalyticsAPI = {
  trackEvent: (data) => api.post('/analytics/workflow-event', data),
};

// PDFs
export const pdfAPI = {
  list: (profileId) => api.get(`/profiles/${profileId}/pdfs`),
  upload: (profileId, formData) => api.post(`/profiles/${profileId}/pdfs`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (pdfId) => api.delete(`/pdfs/${pdfId}`),
  getDownloadUrl: (pdfId) => `${API_BASE}/api/pdfs/${pdfId}/download`,
};

// Customers
export const customerAPI = {
  list: () => api.get('/customers'),
  create: (data) => api.post('/customers', data),
  delete: (customerId) => api.delete(`/customers/${customerId}`),
};

// Campaign Analytics
export const campaignAPI = {
  getAnalytics: (campaignName) => api.get(`/analytics/campaign/${encodeURIComponent(campaignName)}`),
};

// Artifact Catalog (Ecommerce)
export const catalogAPI = {
  list: () => api.get('/catalog'),
  adminList: () => api.get('/admin/catalog'),
  adminCreate: (data) => api.post('/admin/catalog', data),
  adminUpdate: (itemId, data) => api.patch(`/admin/catalog/${itemId}`, data),
  adminDelete: (itemId) => api.delete(`/admin/catalog/${itemId}`),
  adminUploadImage: (itemId, formData) => api.post(`/admin/catalog/${itemId}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminDeleteImage: (itemId) => api.delete(`/admin/catalog/${itemId}/image`),
  adminReorder: (itemIds) => api.post('/admin/catalog/reorder', { item_ids: itemIds }),
};

// Orders (Ecommerce)
export const orderAPI = {
  create: (data) => api.post('/orders', data),
  list: () => api.get('/orders'),
  get: (orderId) => api.get(`/orders/${orderId}`),
  adminList: (status) => api.get(`/admin/orders${status ? `?status=${status}` : ''}`),
  adminGet: (orderId) => api.get(`/admin/orders/${orderId}`),
  adminUpdateStatus: (orderId, status) => api.patch(`/admin/orders/${orderId}/status`, { status }),
};

// Schedules
export const scheduleAPI = {
  list: () => api.get('/schedules'),
  create: (data) => api.post('/schedules', data),
  delete: (scheduleId) => api.delete(`/schedules/${scheduleId}`),
  getActiveProfile: () => api.get('/schedules/active-profile'),
};

// Custom Domains
export const domainAPI = {
  list: () => api.get('/domains'),
  create: (data) => api.post('/domains', data),
  delete: (domainId) => api.delete(`/domains/${domainId}`),
};

// Notifications
export const notificationAPI = {
  list: () => api.get('/notifications'),
  markRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
};

// Video Forwarding
export const videoAPI = {
  forward: (videoId, data) => api.post(`/videos/${videoId}/forward`, data),
};

export default api;
